import { useState, useEffect, useRef, useContext, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './ProductFlow.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const today = () => new Date().toISOString().split('T')[0];

const fmt = (dt) => {
  if (!dt) return '—';
  const d = new Date(dt);
  return d.toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const TYPE_META = {
  INBOUND:    { label: 'Inbound',    color: '#2E7D32', bg: '#e8f5e9', icon: '⬇' },
  TRANSFER:   { label: 'Transfer',   color: '#1565C0', bg: '#e3f2fd', icon: '↔' },
  SALE:       { label: 'Sale',       color: '#B71C1C', bg: '#fce4ec', icon: '🛒' },
  RETURN:     { label: 'Return',     color: '#6A1B9A', bg: '#f3e5f5', icon: '↩' },
  ADJUSTMENT: { label: 'Adjustment', color: '#E65100', bg: '#fff3e0', icon: '✏' },
  WRITE_OFF:  { label: 'Write-off',  color: '#424242', bg: '#f5f5f5', icon: '✕' },
};

/* ── Smart product autocomplete ─────────────────────────── */
const ProductPicker = ({ products, value, onChange }) => {
  const selected = products.find(p => p.id === value) || null;
  const [query, setQuery] = useState('');
  const [open,  setOpen]  = useState(false);
  const wrapRef = useRef(null);

  const filtered = query.trim()
    ? products.filter(p => p.name.toLowerCase().includes(query.toLowerCase()))
    : products;

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (p) => { onChange(p.id); setQuery(''); setOpen(false); };
  const handleClear  = (e) => { e.stopPropagation(); onChange(''); setQuery(''); setOpen(false); };

  return (
    <div className="pf-pp-wrap" ref={wrapRef}>
      <div className="pf-pp-input-row" onClick={() => setOpen(o => !o)}>
        {selected && !open ? (
          <>
            <span className="pf-pp-selected">{selected.name}</span>
            <button type="button" className="pf-pp-clear" onClick={handleClear}>✕</button>
          </>
        ) : (
          <input
            className="pf-pp-input"
            type="text"
            placeholder={selected ? selected.name : 'Search product…'}
            value={query}
            autoFocus={open}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onClick={e => { e.stopPropagation(); setOpen(true); }}
          />
        )}
        <span className="pf-pp-arrow">{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <ul className="pf-pp-dropdown">
          {filtered.length === 0
            ? <li className="pf-pp-empty">No products found</li>
            : filtered.slice(0, 60).map(p => (
              <li
                key={p.id}
                className={`pf-pp-option${p.id === value ? ' pf-pp-option--active' : ''}`}
                onMouseDown={() => handleSelect(p)}
              >
                {p.name}
              </li>
            ))}
        </ul>
      )}
    </div>
  );
};

/* ── Flow node card ──────────────────────────────────────── */
const FlowNode = ({ mv, isLast }) => {
  const meta = TYPE_META[mv.movement_type] || { label: mv.movement_type, color: '#555', bg: '#f5f5f5', icon: '•' };
  const qty  = parseFloat(mv.quantity);

  return (
    <div className="pf-node">
      <div className="pf-node-rail">
        <div className="pf-node-dot" style={{ background: meta.color }} />
        {!isLast && <div className="pf-node-line" />}
      </div>

      <div className="pf-node-card">
        <div className="pf-node-header">
          <span className="pf-node-badge" style={{ background: meta.bg, color: meta.color }}>
            {meta.icon} {meta.label}
          </span>
          <span className="pf-node-qty" style={{ color: meta.color }}>
            {mv.from_location && !mv.to_location ? '−' : mv.to_location && !mv.from_location ? '+' : ''}
            {qty % 1 === 0 ? qty : qty.toFixed(3)} {mv.unit || ''}
          </span>
        </div>

        <div className="pf-node-route">
          {mv.from_location && (
            <span className="pf-node-loc pf-node-loc--from">{mv.from_location}</span>
          )}
          {mv.from_location && mv.to_location && (
            <span className="pf-node-arrow">→</span>
          )}
          {mv.to_location && (
            <span className="pf-node-loc pf-node-loc--to">{mv.to_location}</span>
          )}
        </div>

        <div className="pf-node-meta">
          {mv.created_by && (
            <span className="pf-node-meta-item">👤 {mv.created_by}</span>
          )}
          <span className="pf-node-meta-item">🕐 {fmt(mv.movement_date || mv.created_date)}</span>
          {mv.reference_type && (
            <span className="pf-node-meta-item">
              📋 {mv.reference_type}{mv.reference_id ? ` #${mv.reference_id}` : ''}
            </span>
          )}
          {mv.notes && (
            <span className="pf-node-meta-item pf-node-notes">💬 {mv.notes}</span>
          )}
        </div>
      </div>
    </div>
  );
};

/* ── Product Flow page ───────────────────────────────────── */
const ProductFlow = () => {
  const { user } = useContext(AuthContext);
  const auth = { headers: { Authorization: `Bearer ${user?.token}` } };

  const [products,   setProducts]   = useState([]);
  const [productId,  setProductId]  = useState('');
  const [date,       setDate]       = useState(today());
  const [flow,       setFlow]       = useState([]);
  const [searched,   setSearched]   = useState(false);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/products/`, auth)
      .then(r => setProducts(r.data || []))
      .catch(() => {});
  }, []); // eslint-disable-line

  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault();
    if (!productId) { setError('Please select a product.'); return; }
    setLoading(true); setError(''); setFlow([]); setSearched(false);
    try {
      const res = await axios.get(`${API_URL}/stock/product-flow`, {
        params: { product_id: productId, date },
        ...auth,
      });
      setFlow(res.data || []);
      setSearched(true);
    } catch {
      setError('Failed to load product flow.');
    } finally {
      setLoading(false);
    }
  }, [productId, date]); // eslint-disable-line

  const selectedProduct = products.find(p => p.id === productId);

  return (
    <div className="pf-page">
      <h1 className="pf-title">Product Flow</h1>

      <form className="pf-controls" onSubmit={handleSubmit}>
        <div className="pf-control-group">
          <label className="pf-label">Product</label>
          <ProductPicker products={products} value={productId} onChange={setProductId} />
        </div>
        <div className="pf-control-group">
          <label className="pf-label">Date</label>
          <input
            className="pf-date"
            type="date"
            value={date}
            onChange={e => setDate(e.target.value)}
          />
        </div>
        <button className="pf-submit" type="submit" disabled={loading}>
          {loading ? 'Loading…' : 'Show Flow'}
        </button>
      </form>

      {error && <div className="pf-error">{error}</div>}

      {searched && !loading && (
        <div className="pf-flow-area">
          <p className="pf-flow-heading">
            {selectedProduct?.name} — {new Date(date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
          </p>

          {flow.length === 0 ? (
            <div className="pf-empty">No stock movements found for this product on the selected date.</div>
          ) : (
            <div className="pf-timeline">
              {flow.map((mv, i) => (
                <FlowNode key={mv.id} mv={mv} isLast={i === flow.length - 1} />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductFlow;
